import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures, SalesTypes } from 'app/core/enums/common.enum';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as TotalGrossActions from './total-gross.actions';

@Injectable()
export class TotalGrossEffects {
    private _actions$ = inject(Actions);
    private _totalGrossService = inject(TotalGrossService);
    private _alertService = inject(AlertService);
    private _venueFinancesService = inject(VenueFinancesService);

    messages = {
        getAllError: 'Error getting all Total Grosses',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Update Total Gross
    updateTotalGross$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TotalGrossActions.updateTotalGross),
            switchMap((action) => {
                return this._totalGrossService
                    .updateByLocation(action.data, action.departure)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            return TotalGrossActions.updateTotalGrossSuccess({
                                data: res.data,
                                success: 'Total Gross updated successfully',
                                departure: action.departure,
                            });
                        }),
                        catchError((error) =>
                            of(
                                TotalGrossActions.updateTotalGrossFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Update Total Gross By Location
    updateTotalGrossByLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TotalGrossActions.updateTotalGrossByLocation),
            switchMap((action) => {
                return this._totalGrossService
                    .updateByLocation(action.data, action.departure)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            return TotalGrossActions.updateTotalGrossByLocationSuccess(
                                {
                                    totalGross: res.data,
                                    success: 'Total Gross updated successfully',
                                    playdateId: action.playdateId,
                                    saleType: SalesTypes.SALE_BY_LOCATION,
                                    departure: Departures.BOX_OFFICE,
                                    resource: action.resource,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                TotalGrossActions.updateTotalGrossByLocationFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Update Total Gross By Sales Rep
    updateTotalGrossBySalesRep$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TotalGrossActions.updateTotalGrossBySalesRep),
            switchMap((action) => {
                return this._totalGrossService
                    .updateBySalesRep(action.data)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            return TotalGrossActions.updateTotalGrossBySalesRepSuccess(
                                {
                                    totalGross: res.data,
                                    success: 'Total Gross updated successfully',
                                    playdateId: action.playdateId,
                                    saleType: SalesTypes.SALE_BY_SALE_REP,
                                    departure: Departures.BOX_OFFICE,
                                    resource: action.resource,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                TotalGrossActions.updateTotalGrossBySalesRepFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Validate Total Gross
    validateTotalGross$ = createEffect(() =>
        this._actions$.pipe(
            ofType(TotalGrossActions.validateTotalGross),
            mergeMap((action) => {
                return this._totalGrossService
                    .validateSales(action.playdateId, action.departure)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            return TotalGrossActions.validateTotalGrossSuccess({
                                data: {
                                    locationsWithoutTotalGross:
                                        res.data.locations_without_total_gross,
                                    locationsWithGrossMismatch:
                                        res.data.locations_with_gross_mismatch,
                                    departure: action.departure,
                                },
                                success: 'Total Gross validated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                TotalGrossActions.validateTotalGrossFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onTotalGrossActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    TotalGrossActions.updateTotalGrossSuccess,
                    TotalGrossActions.updateTotalGrossByLocationSuccess,
                    TotalGrossActions.updateTotalGrossBySalesRepSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Get departure
                    const departure =
                        this._totalGrossService.currentDeparture() as Departures;

                    // Reload totals
                    // -------------
                    // Though the total gross doesn't affect the totals,
                    // this is an important step in order to get locations
                    // with gross mismatch
                    this._venueFinancesService.onUpdateFinances = true;

                    if (
                        departure === Departures.CONCESSION ||
                        departure === Departures.NOVELTIES
                    ) {
                        this._totalGrossService.onValidateCnNvFinances(
                            departure
                        );
                    }
                })
            ),
        { dispatch: false }
    );

    onTotalGrossActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    // TotalGrossActions.loadTotalGrossesFailure,
                    // TotalGrossActions.loadTotalGrossFailure,
                    // TotalGrossActions.createTotalGrossFailure,
                    TotalGrossActions.updateTotalGrossFailure,
                    TotalGrossActions.updateTotalGrossByLocationFailure,
                    TotalGrossActions.updateTotalGrossBySalesRepFailure
                    // TotalGrossActions.deleteTotalGrossFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
